import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import styles from './DataTable.module.scss';
export interface DataTableColumn {
  /** The row field this column reads. */
  key: string;
  header: ReactNode;
  /** Keep the header in the DOM for assistive technology, visually hidden. */
  headerHidden?: boolean;
  /** `end` for figures. Defaults to `start`. */
  align?: 'start' | 'end';
}
export type DataTableRow = Readonly<Record<string, ReactNode>>;
export interface DataTableBaseProps {
  /** Required: the scroll region's accessible name, and the table's when it has no caption. */
  label: string;
  /**
   * A visible `<caption>`, which names the table. Only where the page shows
   * one today: a caption is text, and adding one is a content change.
   */
  caption?: ReactNode;
}

/** The table from data: column headers, body rows and optional footer rows. */
export interface DataTableDataProps {
  columns: readonly DataTableColumn[];
  rows: readonly DataTableRow[];
  /** The key of the column whose cells are row headers (`<th scope="row">`). */
  rowHeader?: string;
  /** Footer rows (a `tfoot`), with the same columns. */
  footer?: readonly DataTableRow[];
  children?: never;
}

/**
 * Your own `thead` / `tbody` / `tfoot`, for tables with spans. The scopes are
 * yours: a spanning `scope="rowgroup"` header heads the rest of its row
 * group, so give each group its own `tbody`. `align` is a data-form prop: a
 * children-form table has no end alignment.
 */
export interface DataTableChildrenProps {
  children: ReactNode;
  columns?: never;
  rows?: never;
  rowHeader?: never;
  footer?: never;
}
export type DataTableProps = DataTableBaseProps & (DataTableDataProps | DataTableChildrenProps);
function cellClass(column: DataTableColumn) {
  return column.align === 'end' ? styles['helios-data-table__cell--end'] : undefined;
}
function DataRows({
  columns,
  rows,
  rowHeader
}: {
  columns: readonly DataTableColumn[];
  rows: readonly DataTableRow[];
  rowHeader?: string;
}) {
  return rows.map((row, index) =>
  // Rows are static page data with no id of their own: their order is the
  // key.
  <tr key={index}>
      {columns.map(column => column.key === rowHeader ? <th className={cellClass(column)} key={column.key} scope="row">
            {row[column.key]}
          </th> : <td className={cellClass(column)} key={column.key}>
            {row[column.key]}
          </td>)}
    </tr>);
}

/**
 * A data table (Phase 5 PR 5.0, incubating): the redesign's table look in a
 * keyboard-focusable scroll region, so a table wider than its column scrolls
 * instead of being cut off (O61). The region fades out on a side while it has
 * columns hidden there (O289). Data (`columns`, `rows`, `rowHeader`,
 * `footer`) or your own sections as `children`. Presentational: no
 * `className`, no `style`.
 */
function DataTable(props) {
  const $ = _c(15);
  const {
    label,
    caption
  } = props;
  const t0 = caption ? undefined : label;
  let t1;
  if ($[0] !== caption) {
    t1 = caption ? <caption className={styles["helios-data-table__caption"]}>{caption}</caption> : null;
    $[0] = caption;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== props.children || $[3] !== props.columns || $[4] !== props.footer || $[5] !== props.rowHeader || $[6] !== props.rows) {
    t2 = props.columns ? <><thead><tr>{props.columns.map(_temp)}</tr></thead><tbody><DataRows columns={props.columns} rowHeader={props.rowHeader} rows={props.rows} /></tbody>{props.footer?.length ? <tfoot><DataRows columns={props.columns} rowHeader={props.rowHeader} rows={props.footer} /></tfoot> : null}</> : props.children;
    $[2] = props.children;
    $[3] = props.columns;
    $[4] = props.footer;
    $[5] = props.rowHeader;
    $[6] = props.rows;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t0 || $[9] !== t1 || $[10] !== t2) {
    t3 = <table aria-label={t0} className={styles["helios-data-table__table"]}>{t1}{t2}</table>;
    $[8] = t0;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== label || $[13] !== t3) {
    t4 = <div className={styles["helios-data-table__frame"]}><div aria-label={label} className={styles["helios-data-table"]} data-helios-data-table="" role="region" tabIndex={0}>{t3}</div></div>;
    $[12] = label;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}
function _temp(column) {
  return <th className={cellClass(column)} key={column.key} scope="col">{column.headerHidden ? <span className={styles["helios-data-table__visually-hidden"]}>{column.header}</span> : column.header}</th>;
}
export default DataTable;